import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./CustomizationsPage.scss";
import heroBanner from "../../assets/images/banner-customizations.png";
import Copyright from "../../components/Copyright/Copyright";

const CustomizationsPage = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="feature-page three-column-methods">
      {/* Hero Section */}
      <section className="feature-hero">
        <div className="container">
          <div className="feature-hero-image">
            <img src={heroBanner} alt="Customizations" />
          </div>
        </div>
      </section>

      {/* Customization Overview */}
      <section className="feature-info">
        <div className="container">
          <div className="feature-grid">
            <div className="feature-details">
              <h2>A fully customizable platform</h2>
              <p>
                CashManager adapts to your business needs with extensive customization options.
                From field visibility to form layouts, dashboard widgets to payment details -
                every aspect can be tailored to match your workflow and requirements.
              </p>

              <div className="feature-methods three-columns">
                <div className="feature-method">
                  <div className="method-content">
                    <h3>User interface customization</h3>
                    <p>Show, hide, and rearrange fields in tables and forms to match your workflow preferences.</p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>Company-specific settings</h3>
                    <p>Configure field setups and preferences on a per-company basis for multi-entity management.</p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>Administrative controls</h3>
                    <p>Enable technical fields and advanced features for power users and administrators.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-features">
              <h2>Key customization areas</h2>

              <div className="features-list">
                <div className="feature-item">
                  <h3>Table field management</h3>
                  <p>Show or hide columns and rearrange field order in all data tables throughout the system.</p>
                </div>

                <div className="feature-item">
                  <h3>Form field configuration</h3>
                  <p>Customize edit forms by showing or hiding fields based on user roles and business needs.</p>
                </div>

                <div className="feature-item">
                  <h3>Dashboard customization</h3>
                  <p>Enable specific widgets and arrange dashboard layout according to user preferences and roles.</p>
                </div>

                <div className="feature-item">
                  <h3>Invoice form templates</h3>
                  <p>Create and customize invoice forms with your branding and specific field requirements.</p>
                </div>

                <div className="feature-item">
                  <h3>Email template management</h3>
                  <p>Customize invoicing and reminder email templates with your company branding and messaging.</p>
                </div>

                <div className="feature-item">
                  <h3>Multi-country support</h3>
                  <p>Define additional invoice and email forms for specific countries and regulatory requirements.</p>
                </div>

                <div className="feature-item">
                  <h3>Automation and scheduling</h3>
                  <p>Define automated workflows and schedules for recurring payments, bank statement processing, and other financial tasks.</p>
                  <p className="integration-link">
                    <Link to="/scheduled-tasks">Learn more →</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment & Financial Customization */}
      <section className="supported-items">
        <div className="container">
          <div className="section-header">
            <h2>Payment and financial customization</h2>
            <p>Fine-tune your financial processes with advanced customization options</p>
          </div>

          <div className="items-grid">
            <div className="item">
              <h3>Payment details customization</h3>
              <p>Customize payment reference text separately for local and international payments to match your bank's requirements and improve reconciliation accuracy.</p>
            </div>

            <div className="item">
              <h3>VAT rounding methods</h3>
              <p>Choose between line-by-line or total amount VAT rounding methods to comply with your country's tax regulations and accounting standards.</p>
            </div>

            <div className="item">
              <h3>Allocation criteria management</h3>
              <p>Fully customize allocation criteria separately for customer invoices, supplier invoices, and bank charges to automate your reconciliation process.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="implementation-process">
        <div className="container">
          <div className="section-header">
            <h2>Getting started with customization</h2>
            <p>Set up your customized CashManager environment in just a few steps</p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Initial assessment</h3>
                <p>We analyze your current workflow and identify customization opportunities.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Configuration setup</h3>
                <p>Configure field visibility, form layouts, and dashboard preferences according to your needs.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Template creation</h3>
                <p>Create custom invoice and email templates with your branding and specific requirements.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Testing & training</h3>
                <p>Test all customizations and provide training to ensure smooth adoption.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to customize your financial management?</h2>
            <p>Contact our team to discuss your specific customization needs and create a tailored solution for your business.</p>
            <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
          </div>
        </div>
      </section>

      {/* Copyright Section */}
      <Copyright />
    </div>
  );
};

export default CustomizationsPage;
